import { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Award, ClipboardCheck } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

const AGES = ["U7","U8","U9","U10","U11","U12","U13","U14","U15","U16","U18","Adults"];
const empty = { name: "", phone: "", email: "", level: "", max_age_group: "", is_qualified: false, notes: "" };

export function ProposeRefereeDialog({ open, onClose, onSubmitted }) {
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);

  const canSubmit = form.name.trim().length >= 2;
  const reset = () => setForm(empty);

  const submit = async () => {
    if (!canSubmit) { toast.error("Referee name is required"); return; }
    setBusy(true);
    try {
      await api.post("/proposals/referee", {
        name: form.name.trim(),
        phone: form.phone.trim() || null,
        email: form.email.trim() || null,
        level: form.level.trim() || null,
        max_age_group: form.max_age_group || null,
        is_qualified: !!form.is_qualified,
        notes: form.notes.trim() || null,
      });
      toast.success("Proposal submitted — admins have been notified");
      reset();
      onSubmitted?.();
      onClose?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Couldn't submit proposal");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) { reset(); onClose?.(); } }}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="propose-referee-dialog">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight text-2xl flex items-center gap-2">
            <Award className="w-5 h-5 text-[#83B68B]" /> Propose a new referee
          </DialogTitle>
          <DialogDescription className="text-xs opacity-70">
            Fill in what you know and admins will review before the referee is added to the directory.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 pt-2">
          <div>
            <Label className="text-[10px] tracking-widest uppercase opacity-70">Name <span className="text-[#83B68B]">*</span></Label>
            <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Full name" data-testid="propose-ref-name" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-[10px] tracking-widest uppercase opacity-70">Phone</Label>
              <Input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} placeholder="07…" data-testid="propose-ref-phone" />
            </div>
            <div>
              <Label className="text-[10px] tracking-widest uppercase opacity-70">Email</Label>
              <Input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} placeholder="ref@example.com" data-testid="propose-ref-email" />
            </div>
            <div>
              <Label className="text-[10px] tracking-widest uppercase opacity-70">Level / experience</Label>
              <Input value={form.level} onChange={e => setForm({ ...form, level: e.target.value })} placeholder="FA Level 4 · 3 years" data-testid="propose-ref-level" />
            </div>
            <div>
              <Label className="text-[10px] tracking-widest uppercase opacity-70">Can officiate up to</Label>
              <Select value={form.max_age_group || "any"} onValueChange={v => setForm({ ...form, max_age_group: v === "any" ? "" : v })}>
                <SelectTrigger data-testid="propose-ref-age"><SelectValue placeholder="Any age" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="any">Any age</SelectItem>
                  {AGES.map(a => <SelectItem key={a} value={a}>Up to {a}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex items-center gap-3 rounded-md border border-border px-3 py-2">
            <Switch checked={form.is_qualified} onCheckedChange={v => setForm({ ...form, is_qualified: v })} data-testid="propose-ref-qualified" />
            <div>
              <div className="text-sm font-semibold">Qualified referee</div>
              <div className="text-[11px] opacity-60">Tick if they hold an FA referee qualification.</div>
            </div>
          </div>
          <div>
            <Label className="text-[10px] tracking-widest uppercase opacity-70">Notes for admin</Label>
            <Textarea rows={3} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} placeholder="Anything else the admin should know…" data-testid="propose-ref-notes" />
          </div>
          <div className="flex gap-2 pt-1">
            <button type="button" onClick={() => { reset(); onClose?.(); }} className="flex-1 py-3 rounded-md border border-border text-xs tracking-widest uppercase font-display font-bold hover:border-white/40" data-testid="propose-ref-cancel">Not now</button>
            <button type="button" onClick={submit} disabled={!canSubmit || busy} data-testid="propose-ref-submit" className="btn-volt flex-1 py-3 rounded-md disabled:opacity-40 inline-flex items-center justify-center gap-2">
              <ClipboardCheck className="w-4 h-4" /> {busy ? "Sending…" : "Send for review"}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
